import { SelectOption } from '@act/storybook';
import { DateTime, DurationLike } from 'luxon';
import { getEnvironment } from 'toolkit/environment/environment/environment.util';

const DATE_FORMAT = 'yyyy-MM-dd';
export const DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm';

/** Used when API config omits `MEMBER_STATUS_ALGORITHM_START_DATE` (common in local `.env`). */
const MEMBER_STATUS_ALGORITHM_START_DATE_FALLBACK = '2020-01-01 00:00';

/**
 * Member-status engagement boundary from frontend env (served by API config).
 *
 * @returns Parsed algorithm start; falls back when env is missing or invalid.
 */
export function getMemberStatusAlgorithmStartDate(): DateTime {
  const raw = getEnvironment().memberStatusAlgorithmStartDate;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = DateTime.fromFormat(raw.trim(), DATE_TIME_FORMAT);
    if (parsed.isValid) {
      return parsed;
    }
  }
  return DateTime.fromFormat(
    MEMBER_STATUS_ALGORITHM_START_DATE_FALLBACK,
    DATE_TIME_FORMAT
  );
}

function getReminderDateOptions(): SelectOption<string>[] {
  const today = DateTime.now().startOf('day');
  const getDateString = (duration: DurationLike) =>
    today.plus(duration).toFormat(DATE_FORMAT);

  return [
    { label: 'Tomorrow', value: getDateString({ days: 1 }) },
    {
      label: 'In 2 Days',
      value: getDateString({ days: 2 }),
    },
    {
      label: 'In 1 week',
      value: getDateString({ weeks: 1 }),
    },
    {
      label: 'In 2 Weeks',
      value: getDateString({ weeks: 2 }),
    },
    {
      label: 'In 1 Month',
      value: getDateString({ months: 1 }),
    },
    { label: 'Custom Date/Time', value: 'custom' },
  ];
}

function getScheduledEventTimeOptions(
  requiredOption?: number
): SelectOption<number>[] {
  const startOfDay = DateTime.now().startOf('day');
  return getTimeOptionsHelper({
    start: startOfDay.plus({ hours: 7 }),
    end: startOfDay.plus({ hours: 21 }),
    intervalMins: 30,
    requiredOption,
  });
}

function getOrdinality(day: number) {
  if (day > 3 && day < 21) return 'th';

  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

function getDateOption(date: DateTime): { label: string; value: string } {
  const dateValue = date.toFormat(DATE_FORMAT);

  let monthAndDay = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
  }).format(date.toJSDate());

  const today = DateTime.now().toFormat(DATE_FORMAT);
  const yesterday = DateTime.now().minus({ days: 1 }).toFormat(DATE_FORMAT);
  if (dateValue === today) monthAndDay = 'Today, ' + monthAndDay;
  if (dateValue === yesterday) monthAndDay = 'Yesterday, ' + monthAndDay;

  const dateLabel = monthAndDay + getOrdinality(date.toJSDate().getDate());

  return {
    label: dateLabel,
    value: dateValue,
  };
}

function getDateOptions(otherOption?: number | null): SelectOption<string>[] {
  const algorithmStartDate = getMemberStatusAlgorithmStartDate();

  const dateOptions: { label: string; value: string }[] = [];
  const d = DateTime.now();

  Array.from(Array(10).keys()).forEach(n => {
    const date = d.minus({ days: n });

    if (date >= algorithmStartDate) {
      dateOptions.push(getDateOption(date));
    }
  });

  if (otherOption) {
    const otherDateOption = getDateOption(
      DateTime.fromJSDate(new Date(otherOption))
    );
    const exists = dateOptions.find(d => d.value === otherDateOption.value);

    if (!exists) {
      dateOptions.push(otherDateOption);
    }
  }

  return dateOptions;
}

function getDateString(epochMillis: number): string {
  return epochMillis && DateTime.fromMillis(epochMillis).toFormat(DATE_FORMAT);
}

function epochMillisFromDateString(dateString: string): number {
  if (!dateString) {
    return null;
  }
  const dateTime = DateTime.fromFormat(dateString, DATE_FORMAT);
  return dateTime.toMillis();
}

export function getTimeOptions(
  requiredOption?: number
): SelectOption<number>[] {
  const startOfDay = DateTime.now().startOf('day');
  const tomorrow = startOfDay.plus({ days: 1 });
  return getTimeOptionsHelper({
    start: startOfDay,
    end: tomorrow,
    intervalMins: 15,
    requiredOption,
  });
}

/**
 * Goes to the index of the new start time, and splices the timeOptions array to shift
 * all times preceeding that time to the end of the array.
 *
 * @param newStartTime number - new time in milliseconds to start at index 0
 * @param timeOptions SelectOption[] - the array of currently existing time options
 *
 * @returns SelectOption[] - new time-shifted time options array
 */
export function shiftTimeOptionsStartTime(
  newStartTime: number,
  timeOptions: SelectOption<number>[]
): SelectOption<number>[] {
  const newStartIndex = timeOptions.findIndex(option => {
    return (option.value as number) >= newStartTime;
  });
  return [...timeOptions.splice(newStartIndex), ...timeOptions];
}

function getTimeOptionsHelper(params: {
  start: DateTime;
  end: DateTime;
  intervalMins: number;
  requiredOption?: number;
}): SelectOption<number>[] {
  const requiredValue = getTimeValue(params.requiredOption);

  let cursor = params.start;

  const timeOptions: SelectOption<number>[] = [];
  while (cursor < params.end) {
    const option = {
      label: cursor.toFormat('h:mm a'),
      value: getTimeValue(cursor.toMillis()),
    };

    // If the requiredOption is greater than the last item in the timeOptions array and less than the option we are adding now, add the required option to the list.
    if (
      requiredValue &&
      timeOptions.length > 0 &&
      timeOptions[timeOptions.length - 1].value < requiredValue &&
      requiredValue < option.value
    ) {
      timeOptions.push({
        label: DateTime.fromMillis(params.requiredOption).toFormat('h:mm a'),
        value: requiredValue,
      });
    }

    timeOptions.push(option);

    cursor = cursor.plus({ minutes: params.intervalMins });
  }

  return timeOptions;
}

export const getTimeValue = (epochMillis?: number): number => {
  if (!epochMillis) {
    return null;
  }
  const startOfDayMillis = DateTime.fromMillis(epochMillis)
    .startOf('day')
    .toMillis();
  return epochMillis - startOfDayMillis;
};

export const getMillisFromStartOfDayPlusTime = (
  dateString: string,
  timeMillis?: number
): number => {
  if (!timeMillis) {
    return null;
  }
  const startOfDayMillis = DateTime.fromFormat(dateString, 'MM/dd/yyyy')
    .startOf('day')
    .toMillis();
  return startOfDayMillis + timeMillis;
};

export const getMillisFromStartOfTodayPlusTime = (
  timeMillis?: number
): number => {
  if (!timeMillis) {
    return null;
  }
  const startOfDayMillis = DateTime.now().startOf('day').toMillis();
  return startOfDayMillis + timeMillis;
};

export const scheduledEventDateTimeHelpers = {
  getReminderDateOptions,
  getTimeOptions: getScheduledEventTimeOptions,
  getTimeValueFromEpochMillis: getTimeValue,
};

export const visitDateTimeHelpers = {
  epochMillisFromDateString,
  getDateOptions,
  getTimeOptions,
  getTimeValue,
  getDateString,
};
